import PropTypes from 'prop-types';

import { makeOptionsWithNull } from 'Common/FormHelper';
import {
  ProductionRecordFieldEnum as ProdRecordFields,
  ProductionRecordDetailFieldEnum as ProdRecordDetailFields,
  ProductionRecipeLeftoverEnum as ProdRecipeLeftoverFields,
} from 'Common/FieldEnum';
import { createMap } from 'Common/Helpers/format';
import { ProductionStatusEnum } from 'Common/Constants/Enumeration/ProductionStatusEnum';

export const NUMBER_OF_DAYS_TO_DISPLAY = 5;

export const TEMPERATURES = [
  {
    Description: 'Cook/Prep Target Temp',
    FieldName: ProdRecordDetailFields.TargetTemperature,
    Length: 6,
  },
  {
    Description: 'Hold Target Temp',
    FieldName: ProdRecordDetailFields.HoldTemperature,
    Length: 6,
  },
  {
    Description: 'Time 1st Pan Prepared',
    FieldName: ProdRecordDetailFields.TimeFirstPanPrepared,
    Length: 8,
  },
  {
    Description: 'Food Temp once Prepared',
    FieldName: ProdRecordDetailFields.PreparedTemperature,
    Length: 6,
  },
  {
    Description: 'Food Temp once Removed',
    FieldName: ProdRecordDetailFields.RemovedTemperature,
    Length: 8,
  },
  {
    Description: 'Leftover Temp',
    FieldName: ProdRecordDetailFields.LeftoverTemperature,
    Length: 10,
  },
];

export const TEMPERATURES_MAP = createMap(TEMPERATURES, 'Description');

export const TEMPERATURES_FIELD_NAME_MAP = createMap(TEMPERATURES, 'FieldName');

export const TEMPERATURE_OPTIONS = makeOptionsWithNull(TEMPERATURES, {
  keyKey: 'Description',
  valueKey: 'Description',
  textKeys: ['Description'],
});

export const CHEVRON_COLUMN_WIDTH = 20;

export const PLANNED_STATUS_OPTIONS = [{ text: '', value: '' }].concat(
  Object.keys(ProductionStatusEnum).map((key) => ({
    text: key,
    value: ProductionStatusEnum[key],
  }))
);

export const CommonSectionPropTypes = {
  viewProdRecordIndex: PropTypes.number.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  detailsByRecipeCategory: PropTypes.object.isRequired,
};

export const OMIT_PROD_DETAIL_SAVE_FIELDS = [
  ProdRecordDetailFields.PlannedPercentage,
  ProdRecordDetailFields.ReactKey,
  ProdRecordDetailFields.ArrayIndex,
];

export const NUMERIC_PROD_DETAIL_SAVE_FIELDS = [
  ProdRecordDetailFields.ReimbursableMeals,
  ProdRecordDetailFields.PlannedPreK,
  ProdRecordDetailFields.PlannedAlacarte,
  ProdRecordDetailFields.PlannedAdult,
  ProdRecordDetailFields.Offered,
  ProdRecordDetailFields.OfferedAlacarte,
  ProdRecordDetailFields.OfferedAdult,
  ProdRecordDetailFields.Served,
  ProdRecordDetailFields.ServedAlacarte,
  ProdRecordDetailFields.ServedAdult,
  ProdRecordDetailFields.Discard,
];

export const RECIPE_LEFTOVER_FIELDS_TO_LEFTOVER_DETAIL_FIELDS = [
  ProdRecipeLeftoverFields.Portions,
  ProdRecipeLeftoverFields.RecipeLeftoverId,
  ProdRecipeLeftoverFields.SiteId,
  ProdRecipeLeftoverFields.UseByDT,
];

export const CommonProdRecordGridFields = [
  ProdRecordDetailFields.ReactKey,
  ProdRecordDetailFields.IngredientId,
  ProdRecordDetailFields.IsSingleIngredientRecipe,
  ProdRecordDetailFields.RecipeId,
  ProdRecordDetailFields.ArrayIndex,
  ProdRecordDetailFields.IsLeftover,
];

export const MealSectionGridFields = [
  ...CommonProdRecordGridFields,
  ProdRecordDetailFields.Name,
  ProdRecordDetailFields.Fruit,
  ProdRecordDetailFields.DarkGreenVeg,
  ProdRecordDetailFields.RedOrangeVeg,
  ProdRecordDetailFields.LegumesVeg,
  ProdRecordDetailFields.StarchyVeg,
  ProdRecordDetailFields.OtherVeg,
  ProdRecordDetailFields.Grain,
  ProdRecordDetailFields.MeatAlt,
  ProdRecordDetailFields.Milk,
  ProdRecordDetailFields.QtyOfPortion,
  ProdRecordDetailFields.MeasureOfPortion,
];

export const TemperatureSectionGridFields = [
  ...CommonProdRecordGridFields,
  ProdRecordDetailFields.Comments,
  ProdRecordDetailFields.TargetTemperature,
  ProdRecordDetailFields.HoldTemperature,
  ProdRecordDetailFields.TimeFirstPanPrepared,
  ProdRecordDetailFields.PreparedTemperature,
  ProdRecordDetailFields.RemovedTemperature,
  ProdRecordDetailFields.LeftoverTemperature,
];

export const PlannedSectionGridFields = [
  ...CommonProdRecordGridFields,
  ProdRecordDetailFields.ReimbursableMeals,
  ProdRecordDetailFields.PlannedPercentage,
  ProdRecordDetailFields.PlannedPreK,
  ProdRecordDetailFields.PlannedAlacarte,
  ProdRecordDetailFields.PlannedAdult,
  ProdRecordDetailFields.IngredientAmount,
  ProdRecordDetailFields.Kitchen,
];

export const OfferedSectionGridFields = [
  ...CommonProdRecordGridFields,
  ProdRecordDetailFields.Offered,
  ProdRecordDetailFields.OfferedPreK,
  ProdRecordDetailFields.OfferedAlacarte,
  ProdRecordDetailFields.OfferedAdult,
];

export const ServedSectionGridFields = [
  ...CommonProdRecordGridFields,
  ProdRecordDetailFields.Served,
  ProdRecordDetailFields.ServedAdult,
  ProdRecordDetailFields.ServedPreK,
  ProdRecordDetailFields.ServedAlacarte,
  ProdRecordDetailFields.Leftover,
  ProdRecordDetailFields.Discard,
  ProdRecordDetailFields.AvailableReserve,
  ProdRecordDetailFields.ServingLine,
];

export const ProductionPlanFields = [
  ProdRecordFields.ServedStudents,
  ProdRecordFields.ServedPreK,
  ProdRecordFields.ServedSFSAdult,
  ProdRecordFields.ServedCatered,
  ProdRecordFields.ServedOther,
  ProdRecordFields.ServedSnacks,
  ProdRecordFields.OfferVsServe,
  ProdRecordFields.OfferVsServeNotes,
  ProdRecordFields.IsWaterAvailable,
  ProdRecordFields.PersonnelHealthy,
  ProdRecordFields.PersonnelCleanAppearance,
  ProdRecordFields.PersonnelWashingHands,
  ProdRecordFields.PersonnelNoBareHandContact,
  ProdRecordFields.PersonnelThermometersChecked,
  ProdRecordFields.PersonnelNotes,
  ProdRecordFields.ServedNotes,
  ProdRecordFields.FeedingFigureOffered,
];

export const Modals = {
  ProductionPlan: 'Plan',
  Print: 'Print',
  Close: 'Close',
};

export const LeftoverFieldsMap = {
  [ProdRecordDetailFields.Offered]: ProdRecordDetailFields.Offered,
  [ProdRecordDetailFields.OfferedAlacarte]:
    ProdRecordDetailFields.OfferedAlacarte,
  [ProdRecordDetailFields.OfferedAdult]: ProdRecordDetailFields.OfferedAdult,
  [ProdRecordDetailFields.Served]: ProdRecordDetailFields.Served,
};

export const RowHeightOffset = 2;
export const RecipeGridElementIndex = 0;
export const PlannedGridElementIndex = 1;
export const OfferedGridElementIndex = 2;
export const ServedGridElementIndex = 3;
export const TemperatureGridElementIndex = 4;



// WEBPACK FOOTER //
// ./src/pages/manageproduction/helpers/Constants.js